import httpCommon, { httpFormData } from "./http-common";

// Upload document for AI processing
const uploadDocument = (data: FormData) => {
  return httpFormData.post("/v1/add/documentUpload", data);
};

// Get document processing status
const getDocumentProcessingStatus = (documentUploadId: number) => {
  return httpCommon.get(`/v1/get/document-processing-status/${documentUploadId}`);
};

// Get document extraction results
const getDocumentExtraction = (documentUploadId: number) => {
  return httpCommon.get(`/v1/get/document-extraction/${documentUploadId}`);
};

// Get all business documents
const getBusinessDocuments = (businessId: number, skip = 0, limit = 50) => {
  return httpCommon.get(`/v1/get/business-documents/${businessId}?skip=${skip}&limit=${limit}`);
};

// Get document upload by ID
const getDocumentUploadById = (id: number) => {
  return httpCommon.get(`/v1/get/documentUpload/${id}`);
};

// Update document upload
const updateDocumentUpload = (id: number, data: any) => {
  return httpCommon.put(`/v1/update/documentUpload/${id}`, data);
};

// Delete document upload
const deleteDocumentUpload = (id: number) => {
  return httpCommon.delete(`/v1/delete/documentUpload/${id}`);
};

// Reprocess document
const reprocessDocument = (documentUploadId: number) => {
  return httpCommon.post(`/v1/reprocess/document/${documentUploadId}`);
};

// Get processing queue status
const getProcessingQueueStatus = () => {
  return httpCommon.get("/v1/get/processing-queue-status");
};

// Add document to processing queue
const addToProcessingQueue = (data: any) => {
  return httpCommon.post("/v1/utils/add-to-processing-queue", data);
};

// Processing Queue Management
const getProcessingQueue = (businessId: number) => {
  return httpCommon.get(`/v1/get/processing-queue?businessId=${businessId}`);
};

const getQueueStats = (businessId: number) => {
  return httpCommon.get(`/v1/get/queue-stats?businessId=${businessId}`);
};

const getProcessingMetrics = (businessId: number) => {
  return httpCommon.get(`/v1/get/processing-metrics?businessId=${businessId}`);
};

const pauseProcessingQueue = (businessId: number) => {
  return httpCommon.post(`/v1/utils/pause-processing-queue`, { businessId });
};

const resumeProcessingQueue = (businessId: number) => {
  return httpCommon.post(`/v1/utils/resume-processing-queue`, { businessId });
};

const retryQueueItem = (itemId: number) => {
  return httpCommon.post(`/v1/utils/retry-queue-item/${itemId}`);
};

const removeFromQueue = (itemId: number) => {
  return httpCommon.delete(`/v1/delete/queue-item/${itemId}`);
};

// Bulk operations
const bulkReprocessDocuments = (documentIds: number[]) => {
  return httpCommon.post('/v1/bulk/reprocess-documents', { documentIds });
};

const bulkDeleteDocuments = (documentIds: number[]) => {
  return httpCommon.post('/v1/bulk/delete-documents', { documentIds });
};

const bulkUpdateStatus = (documentIds: number[], status: string) => {
  return httpCommon.post('/v1/bulk/update-document-status', { documentIds, status });
};

const bulkExportDocuments = (documentIds: number[], format: 'csv' | 'excel' | 'pdf') => {
  return httpCommon.post(`/v1/bulk/export-documents/${format}`, { documentIds }, {
    responseType: 'blob'
  });
};

const bulkAssignDocumentType = (documentIds: number[], documentTypeId: number) => {
  return httpCommon.post('/v1/bulk/assign-document-type', { documentIds, documentTypeId });
};

const bulkMoveToFolder = (documentIds: number[], folderId: number) => {
  return httpCommon.post('/v1/bulk/move-to-folder', { documentIds, folderId });
};

const bulkAddTags = (documentIds: number[], tags: string[]) => {
  return httpCommon.post('/v1/bulk/add-tags', { documentIds, tags });
};

const bulkRemoveTags = (documentIds: number[], tags: string[]) => {
  return httpCommon.post('/v1/bulk/remove-tags', { documentIds, tags });
};

// Dashboard and analytics methods
const getDashboardStats = (businessId: number, timeRange: string = '24h') => {
  return httpCommon.get(`/v1/get/document-dashboard-stats/${businessId}?timeRange=${timeRange}`);
};

const getProcessingAnalytics = (businessId: number, startDate?: string, endDate?: string) => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  
  return httpCommon.get(`/v1/get/processing-analytics/${businessId}?${params.toString()}`);
};

const getDocumentTypeBreakdown = (businessId: number) => {
  return httpCommon.get(`/v1/get/document-type-breakdown/${businessId}`);
};

const getProcessingTrends = (businessId: number, period: 'daily' | 'weekly' | 'monthly' = 'daily') => {
  return httpCommon.get(`/v1/get/processing-trends/${businessId}?period=${period}`);
};

const getErrorAnalytics = (businessId: number) => {
  return httpCommon.get(`/v1/get/error-analytics/${businessId}`);
};

const getUserActivity = (businessId: number, startDate?: string, endDate?: string) => {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  
  return httpCommon.get(`/v1/get/user-activity/${businessId}?${params.toString()}`);
};

const exportProcessingReport = (businessId: number, format: 'pdf' | 'excel' | 'csv', filters: any) => {
  return httpCommon.post(`/v1/export/processing-report/${businessId}/${format}`, filters, {
    responseType: 'blob'
  });
};

const DocumentUploadsService = {
  uploadDocument,
  getDocumentProcessingStatus,
  getDocumentExtraction,
  getBusinessDocuments,
  getDocumentUploadById,
  updateDocumentUpload,
  deleteDocumentUpload,
  reprocessDocument,
  getProcessingQueueStatus,
  addToProcessingQueue,
  getProcessingQueue,
  getQueueStats,
  getProcessingMetrics,
  pauseProcessingQueue,
  resumeProcessingQueue,
  retryQueueItem,
  removeFromQueue,
  // Bulk operations
  bulkReprocessDocuments,
  bulkDeleteDocuments,
  bulkUpdateStatus,
  bulkExportDocuments,
  bulkAssignDocumentType,
  bulkMoveToFolder,
  bulkAddTags,
  bulkRemoveTags,
  // Analytics
  getDashboardStats,
  getProcessingAnalytics,
  getDocumentTypeBreakdown,
  getProcessingTrends,
  getErrorAnalytics,
  getUserActivity,
  exportProcessingReport
};

export default DocumentUploadsService; 